// src/api/apiClient.js
//const API_BASE = 'https://alpha-api-artis.armexoil.cz'; // development
// src/api/apiClient.js

// Prod URL, případně z env
const API_BASE_OPEN = 'https://api-artis.armexoil.cz/api/open/';
// src/api/apiClient.js
const API_BASE = 'https://api-artis.armexoil.cz/api/v1/';

// Jednoduchý typ chyby z API
export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

// --- ACCESS TOKEN STORAGE ---

let accessToken = null;
let accessTokenExpiry = null; 

const USE_SESSION_STORAGE = true;
const SESSION_KEY = 'artis_access_token';
const SESSION_KEY_EXP = 'artis_access_token_exp';

if (USE_SESSION_STORAGE && typeof window !== 'undefined') {
  const stored = window.sessionStorage.getItem(SESSION_KEY);
  const storedExp = window.sessionStorage.getItem(SESSION_KEY_EXP);
  if (stored && storedExp) {
    accessToken = stored;
    accessTokenExpiry = Number(storedExp) || null;
  }
}

export function setAccessToken(token, expiresInSeconds) {
  accessToken = token || null;
  accessTokenExpiry = expiresInSeconds
    ? Date.now() + expiresInSeconds * 1000
    : null;

  if (USE_SESSION_STORAGE && typeof window !== 'undefined') {
    if (token) {
      window.sessionStorage.setItem(SESSION_KEY, token);
      if (accessTokenExpiry) {
        window.sessionStorage.setItem(SESSION_KEY_EXP, String(accessTokenExpiry));
      }
    } else {
      window.sessionStorage.removeItem(SESSION_KEY);
      window.sessionStorage.removeItem(SESSION_KEY_EXP);
    }
  }
}

export function getAccessToken() {
  return accessToken;
}

export function clearAccessToken() {
  setAccessToken(null, null);
}

export function isAccessTokenExpired(offsetSeconds = 30) {
  if (!accessToken || !accessTokenExpiry) return true;
  return Date.now() >= accessTokenExpiry - offsetSeconds * 1000;
}

// --- SILENT REFRESH PŘES /refresh ---

async function refreshAccessTokenSilently() {
  try {
    const res = await fetch(`${API_BASE}refresh`, {
      method: 'POST',
      credentials: 'include', 
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      return false;
    }

    const data = await res.json().catch(() => ({}));

    if (!data || !data.access_token) {
      return false;
    }

    setAccessToken(data.access_token, data.expires_in);
    return true;
  } catch (e) {
    return false;
  }
}

// --- CORE REQUEST FUNKCE ---

async function baseApiRequest(
  endpoint,
  {
    method = 'GET',
    data = null,
    token = null,
    skipAuth = false, 
    signal,
    _retry = false,
  } = {}
) {
  const url = `${API_BASE.replace(/\/+$/, '')}/${endpoint.replace(/^\/+/, '')}`;

  const headers = {};

  let body = null;
  if (data != null) {
    if (data instanceof FormData) {
      body = data;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(data);
    }
  }

  // Authorization header pokud není skipAuth
  if (!skipAuth) {
    const authToken = token || getAccessToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
  }

  const config = {
    method,
    headers,
    body,
    credentials: 'include', // kvůli refresh cookie
    signal,
  };

  const response = await fetch(url, config);

  // 401 → zkusit silent refresh + jednou retry
  if (response.status === 401 && !_retry && !skipAuth) {
    const refreshed = await refreshAccessTokenSilently();
    if (refreshed) {
      return baseApiRequest(endpoint, {
        method,
        data,
        token,
        skipAuth,
        signal,
        _retry: true,
      });
    }
  }

  const text = await response.text();
  let parsed = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch (_) {
      parsed = text;
    }
  }

  if (!response.ok) {
    const msg =
      (parsed && parsed.message) ||
      `Něco se pokazilo chyba: ${response.status}`;
    throw new ApiError(msg, response.status, parsed);
  }

  return parsed;
}

// Public API – původní signatura pro kompatibilitu
export async function apiRequest(
  endpoint,
  method = 'GET',
  data = null,
  token = null
) {
  return baseApiRequest(endpoint, { method, data, token });
}

// --- LOGIN / LOGOUT / BOOTSTRAP ---

export const login = async (credentials) => {
  const response = await baseApiRequest('login', {
    method: 'POST',
    data: credentials,
    skipAuth: true,
  });

  if (!response) {
    throw new Error('Login failed');
  }

  if (response.requires_2fa) {
    return response;
  }

  if (!response.access_token) {
    throw new Error('Login failed, no access_token received');
  }

  setAccessToken(response.access_token, response.expires_in);

  return response;
};

export const logout = async () => {
  try {
    await baseApiRequest('logout', {
      method: 'POST',
      skipAuth: true,
    });
  } finally {
    clearAccessToken();
  }
};

/**
 * Volat při startu appky
 * - zkusí /refresh pomocí cookie
 * - když uspěje → nastaví access token a vrátí true
 */
export const bootstrapAuth = async () => {
  const ok = await refreshAccessTokenSilently();
  return ok;
};

export const getStations = () =>
  apiRequest('stations', 'GET');

  export const getListStations = () =>
    apiRequest('stations/list', 'GET');
  

export const getStationDetail = (stationId) =>
  apiRequest(`stations/${stationId}/detail`, 'GET');


export const verifyTwoFactorLogin = async ({ challenge_token, code }) => {
  const response = await baseApiRequest('login/2fa/verify', {
    method: 'POST',
    data: {
      challenge_token,
      code,
    },
    skipAuth: true,
  });

  if (!response || !response.access_token) {
    throw new Error('2FA verification failed, no access_token received');
  }

  setAccessToken(response.access_token, response.expires_in);

  return response;
};